import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { AuthService } from "src/app/shared/services/auth.service";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { ValidEmailRegex } from "../../../shared/regex";

/**
 * Interface used to save the login data on the local storage
 */
interface loginData {
  email: string | null;
  rememberMe: boolean;
}

/**
 * Login Component to access by email or third party provideer.
 * Uses Auth for Firebase authentication.
 * Can saves (Remember me) login information: Only email for security reasons
 */
@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class LoginComponent implements OnInit {
  /** Form object for login input */
  protected loginForm: FormGroup;

  /** Email regex */
  protected validEmailRegex = ValidEmailRegex;

  /**
   * Chacks for errors Flag
   * Use to check for error only after an action. So the error messages of the form
   * will not show until and action is taken.
   */
  protected checkErrors: boolean = false;

  /** Flag to check if rememeber me option is selected or not */
  protected _rememberMe: boolean;

  /** Constructor
   * @param {AuthService} authService Authenbtication service
   */
  constructor(private authService: AuthService, private router: Router) {
    // Set's up the login form
    this.loginForm = new FormGroup({
      email: new FormControl<string>("", {
        validators: [Validators.required, Validators.pattern(ValidEmailRegex)],
      }),
      password: new FormControl<string>("", {
        validators: [Validators.required],
      }),
      rememberMe: new FormControl<boolean>(false),
    });
  }

  ngOnInit() {
    // Loads saves data if exists
    this.checkSavedData();
  }

  /**
   * Gets a value from the form control
   * @param controlName The name of the control in the form to get.
   * @returns The expected control or null if the control does not exists.
   */
  getFormControl(controlName: string): AbstractControl | null {
    return this.loginForm.get(controlName);
  }

  signIn() {
    // Activate the check for errors
    this.checkErrors = true;

    // Signs in only when input form is valid
    if (this.loginForm.valid) {
      // If remember me is selected saves data
      if (this.getFormControl("rememberMe")?.value) {
        this.rememberMe();
      } else {
        // No remember me is selected so data must be forgoten
        this.forgetMe();
      }

      // Signs in using auth service
      this.authService._signInWithEmailAndPassword(
        this.getFormControl("email")?.value,
        this.getFormControl("password")?.value
      );
    }
  }

  /** Redirects the user to the registration page */
  register() {
    this.router.navigate(["/authentication/register"]);
  }

  /** Redirects the user to the recovery password page */
  forgotPassword() {
    this.router.navigate(["/authentication/forgetpassword"]);
  }

  /**
   * Saves login information on the web browser.
   * Only the Email will be saved for security reasons.
   * */
  rememberMe() {
    // Saves data on the login variable
    var data: loginData = {
      email: this.getFormControl("email")?.value!,
      rememberMe: true,
    };
    localStorage.setItem(
      environment.loginRememberMeVariable,
      JSON.stringify(data)
    );
  }

  /**
   * Deletes the remember me information
   */
  forgetMe() {
    localStorage.removeItem(environment.loginRememberMeVariable);
  }

  /**
   * Checks if remember me data is saved on local storage
   * In that cas preload the saved data
   */
  checkSavedData() {
    // Checks if data is saved on local storage
    if (localStorage.getItem(environment.loginRememberMeVariable) !== null) {
      var data: loginData = JSON.parse(
        localStorage.getItem(environment.loginRememberMeVariable)!
      ) as loginData;

      // PreloadData
      this.getFormControl("email")?.setValue(data.email);
      this.getFormControl("rememberMe")?.setValue(data.rememberMe);
    }
  }
}
